import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { useAuthStore } from '../../stores/authStore';
import { usePermission } from '../../lib/rbac/usePermission';
import { getRoleDisplayName } from '../../lib/api/auth.api';
import { withAreaPrefix } from '../../lib/area-routes';

/** Lấy 1–2 chữ cái đầu từ tên/email để hiển thị avatar mặc định. */
function getInitials(name: string | null | undefined, email: string | undefined): string {
  const raw = (name || email || '').trim();
  if (!raw) return 'U';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0].charAt(0);
    const last = (parts.at(-1) ?? '').charAt(0);
    return (first + last).toUpperCase();
  }
  return raw.charAt(0).toUpperCase();
}

/** Cycle-add icon: circle with plus (quick add) */
function CycleAddIcon({ className }: { readonly className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}

const ADD_MENU_ITEMS: Array<{ label: string; path: string; scope: string }> = [];

export function AppHeader() {
  const { t } = useTranslation('common');
  const { user, logout, isAuthenticated, isLoading } = useAuthStore();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [avatarError, setAvatarError] = useState(false);
  const hasAvatar = Boolean(user?.avatarUrl?.trim());
  const showInitials = !hasAvatar || avatarError;
  const initials = getInitials(user?.name ?? null, user?.email);
  const userLoading = isAuthenticated && (isLoading || !user);

  useEffect(() => {
    setAvatarError(false);
  }, [user?.id, user?.avatarUrl]);

  const canAddGame = usePermission('game:create');
  const canAddProduct = usePermission('product:create');
  const canAddOrder = usePermission('order:create');
  const canAddInventory = usePermission('inventory:create');
  const addItems = ADD_MENU_ITEMS.filter(
    item =>
      (item.scope === 'game:create' && canAddGame) ||
      (item.scope === 'product:create' && canAddProduct) ||
      (item.scope === 'order:create' && canAddOrder) ||
      (item.scope === 'inventory:create' && canAddInventory)
  );
  const roleDisplayName = getRoleDisplayName(user?.role ?? null);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-30 w-full bg-muted/80 backdrop-blur supports-[backdrop-filter]:bg-muted/60 border-b border-border">
      <div className="flex h-12 items-center px-4 w-full">
        <div className="flex-1" />

        {/* Right side */}
        <div className="flex items-center gap-4 ml-auto">
          {/* Quick Add (cycle-add) - before notification, always visible */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setIsAddMenuOpen(!isAddMenuOpen);
                setIsUserMenuOpen(false);
              }}
              className="flex items-center justify-center p-2 rounded-lg hover:bg-muted transition-colors text-foreground border border-border"
              aria-label={t('quick_add')}
              aria-expanded={isAddMenuOpen}
            >
              <CycleAddIcon />
            </button>
            {isAddMenuOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-card rounded-md shadow-lg border border-border py-1 z-50">
                {addItems.length > 0 ? (
                  addItems.map(({ label, path }) => (
                    <Link
                      key={path}
                      to={withAreaPrefix(path, globalThis.location.pathname)}
                      className="block px-4 py-2 text-sm text-foreground hover:bg-muted"
                      onClick={() => setIsAddMenuOpen(false)}
                    >
                      {label}
                    </Link>
                  ))
                ) : (
                  <p className="px-4 py-2 text-sm text-muted-foreground">{t('no_quick_actions')}</p>
                )}
              </div>
            )}
          </div>

          {/* Notifications */}
          <button
            type="button"
            className="relative p-2 rounded-lg hover:bg-muted transition-colors text-foreground"
            aria-label={t('notifications')}
          >
            <span className="text-xl">🔔</span>
            <span className="absolute top-0 right-0 w-2 h-2 bg-destructive rounded-full" />
          </button>

          <LanguageSwitcher />

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-foreground"
            aria-label={t('toggle_theme')}
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </button>

          {/* User Menu */}
          <div className="relative">
            {userLoading ? (
              <>
                <div
                  className="flex items-center gap-2 p-2"
                  aria-busy="true"
                  aria-label={t('loading_user')}
                >
                  <div className="w-8 h-8 rounded-full bg-muted animate-pulse shrink-0" />
                </div>
                <div className="flex flex-col items-start min-w-0 max-w-[140px]">
                  <span className="text-sm font-medium truncate w-full">
                    {user?.name || user?.email || ''}
                  </span>
                  {roleDisplayName && (
                    <span className="text-xs text-muted-foreground truncate w-full">
                      {roleDisplayName}
                    </span>
                  )}
                  <div className="flex flex-col gap-1 min-w-0 max-w-[140px]">
                    <div className="h-3.5 w-20 bg-muted rounded animate-pulse" />
                    <div className="h-3 w-14 bg-muted rounded animate-pulse" />
                  </div>
                </div>
              </>
            ) : (
              <button
                onClick={() => {
                  setIsUserMenuOpen(!isUserMenuOpen);
                  setIsAddMenuOpen(false);
                }}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors text-foreground"
              >
                {showInitials ? (
                  <div
                    className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium shrink-0"
                    aria-hidden
                  >
                    {initials}
                  </div>
                ) : (
                  <img
                    src={user?.avatarUrl ?? ''}
                    alt={user?.name || user?.email || ''}
                    className="w-8 h-8 rounded-full object-cover shrink-0"
                    onError={() => setAvatarError(true)}
                  />
                )}
                <div className="flex flex-col items-start min-w-0 max-w-[140px]">
                  <span className="text-sm font-medium truncate w-full">
                    {user?.name || user?.email || ''}
                  </span>
                  {roleDisplayName && (
                    <span className="text-xs text-muted-foreground truncate w-full">
                      {roleDisplayName}
                    </span>
                  )}
                </div>
              </button>
            )}

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-card rounded-md shadow-lg border border-border py-1 z-50">
                <div className="px-4 py-2 border-b border-border">
                  <p className="text-sm font-medium text-foreground">
                    {user?.name || user?.email || t('user_fallback')}
                  </p>
                  {user?.email && user?.name && (
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  )}
                  {roleDisplayName && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('role_label', { role: roleDisplayName })}
                    </p>
                  )}
                </div>
                <Link
                  to={withAreaPrefix('/profile', globalThis.location.pathname)}
                  className="block px-4 py-2 text-sm text-foreground hover:bg-muted"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  {t('profile')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted"
                >
                  {t('logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
